/**
 * AutopilotHistory — Historie aller woechentlichen Autopilot-Laeufe (Meta + Google)
 * mit allen KI-Empfehlungen und automatisch ausgefuehrten Aktionen zum Nachlesen.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, History, Zap } from "lucide-react";
import AutopilotWeeklyReport from "@/components/AutopilotWeeklyReport";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_BADGE = {
  google: { label: "Google Ads", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  meta: { label: "Meta Ads", cls: "bg-indigo-100 text-indigo-700 border-indigo-200" },
};
const MODE_LABEL = { suggest: "Vorschlagen", auto: "Vollautomatik", off: "Aus" };
const SEV = { high: "🔴", medium: "🟡", info: "ℹ️" };

export default function AutopilotHistory() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("all");
  const [runs, setRuns] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    axios.get(`${API}/companies`, { withCredentials: true })
      .then((r) => setCompanies(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 30 };
    if (companyId !== "all") params.company_id = companyId;
    axios.get(`${API}/ads/autopilot/runs`, { params, withCredentials: true })
      .then((r) => setRuns(r.data.runs || []))
      .catch(() => setRuns([]))
      .finally(() => setLoading(false));
  }, [companyId]);

  return (
    <div className="p-6 space-y-4" data-testid="autopilot-history">
      <AutopilotWeeklyReport />
      <div className="flex flex-wrap items-center gap-3">
        <History className="w-5 h-5 text-[var(--studio-text-muted)]" />
        <div>
          <h2 className="text-base font-semibold">Autopilot-Historie</h2>
          <p className="text-xs text-[var(--studio-text-muted)]">
            Jeden Montag 06:30 analysiert die KI alle Kampagnen (Meta + Google) der Firmen mit aktivem Autopilot. Hier sind alle Läufe mit sämtlichen Empfehlungen zum Nachlesen.
          </p>
        </div>
        <div className="flex-1" />
        <Select value={companyId} onValueChange={setCompanyId}>
          <SelectTrigger className="w-56" data-testid="autopilot-history-company-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Firmen</SelectItem>
            {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading && <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[var(--studio-text-muted)]" /></div>}

      {runs?.length === 0 && !loading && (
        <Card><CardContent className="py-8 text-center text-sm text-[var(--studio-text-muted)]" data-testid="autopilot-history-empty">
          Noch keine Autopilot-Läufe. Aktiviere den Autopilot im Google- oder Meta-Tab («Wöchentlich vorschlagen» oder «Vollautomatik») — der erste Lauf erfolgt am nächsten Montag um 06:30.
        </CardContent></Card>
      )}

      {runs?.map((r, i) => {
        const pb = PLATFORM_BADGE[r.platform] || PLATFORM_BADGE.meta;
        const isOpen = expanded === i;
        const recos = r.recommendations || [];
        return (
          <Card key={r.id || i} data-testid={`autopilot-run-${i}`}>
            <CardHeader className="pb-2 cursor-pointer" onClick={() => setExpanded(isOpen ? null : i)}>
              <CardTitle className="text-sm flex flex-wrap items-center gap-2">
                <Badge variant="outline" className={`text-[10px] ${pb.cls}`}>{pb.label}</Badge>
                <span className="font-semibold">{r.company_name}</span>
                <span className="text-xs font-normal text-[var(--studio-text-muted)]">
                  {new Date(r.created_at).toLocaleString("de-CH", { dateStyle: "medium", timeStyle: "short" })}
                </span>
                <Badge variant="outline" className="text-[10px]">{MODE_LABEL[r.mode] || r.mode}</Badge>
                <div className="flex-1" />
                <span className="text-xs font-normal text-[var(--studio-text-muted)]">{recos.length} Empfehlungen</span>
                {(r.executed || []).length > 0 && (
                  <Badge className="text-[10px] bg-amber-100 text-amber-700 border-amber-200 gap-1">
                    <Zap className="w-3 h-3" /> {(r.executed || []).length} automatisch ausgeführt
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            {isOpen && (
              <CardContent className="space-y-2 pt-0">
                {recos.map((rec, j) => (
                  <div key={j} className="border border-[var(--studio-border)] rounded-lg p-2.5 text-sm" data-testid={`autopilot-run-${i}-reco-${j}`}>
                    <div className="font-medium text-xs">{SEV[rec.severity] || SEV.info} {rec.title}</div>
                    <div className="text-xs mt-1 text-[var(--studio-text-muted)]">{rec.recommendation}</div>
                  </div>
                ))}
                {(r.executed || []).length > 0 && (
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                    ⚡ Automatisch pausiert: {(r.executed || []).join(", ")} — Undo in der KI-Historie möglich.
                  </div>
                )}
                {recos.length === 0 && <p className="text-xs text-[var(--studio-text-muted)]">Keine Empfehlungen in diesem Lauf.</p>}
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
}
